/**
 * Feature E07-F05 — Evidence Drawer typed api-client + Query hooks (web side).
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` (research-evidence.ts) so the SPA speaks the
 * exact same shapes as the Fastify API (routes/research-evidence.ts).
 *
 * Endpoints (routes/research-evidence.ts, mounted under /research by the Wire pass):
 *   GET  /research/:requestId/companies/:rowId/evidence            (T01/T02/T04/T06)
 *   POST /research/:requestId/companies/:rowId/request-more-info   (T03)
 *
 * Each response is validated against the shared schema at the boundary so a
 * drifted shape fails loudly rather than rendering garbage. "Request more info"
 * writes the freshly-returned drawer back into the cached query (so the drawer
 * re-renders the upgraded criterion + the green trust banner without a refetch);
 * it also invalidates the Review list so the row loses its amber needs-human tint.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  evidenceDrawerResponseSchema,
  requestMoreInfoResponseSchema,
  type EvidenceDrawerResponse,
  type RequestMoreInfoResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { reviewKeys } from './researchReviewApi.js';

// ---------------------------------------------------------------------------
// query keys (feature-local)
// ---------------------------------------------------------------------------

export const evidenceKeys = {
  all: ['research', 'evidence'] as const,
  drawer: (requestId: number, rowId: number) => ['research', 'evidence', requestId, rowId] as const,
} as const;

// ---------------------------------------------------------------------------
// fetchers
// ---------------------------------------------------------------------------

/** Fetch the evidence-drawer DTO for one long-list row (T01/T02/T04/T06). */
export async function fetchEvidenceDrawer(
  requestId: number,
  rowId: number,
): Promise<EvidenceDrawerResponse> {
  const result = await apiClient.get<unknown>(
    `/research/${requestId}/companies/${rowId}/evidence`,
    { command: 'research_evidence_drawer' },
  );
  return evidenceDrawerResponseSchema.parse(unwrap(result));
}

/**
 * Run "Request more info" — re-checks the weakest criterion against the
 * company's own website (E06-F05-T09) and, on a citable confirmation, upgrades it
 * to a cited PASS and clears needs_human (T03). Returns which criterion was
 * upgraded (null when none), a `degraded` flag (no AI model is configured, so the
 * check could not run — fixed in Admin AI settings), and the re-projected drawer.
 */
export async function requestMoreInfo(
  requestId: number,
  rowId: number,
): Promise<RequestMoreInfoResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/companies/${rowId}/request-more-info`,
    undefined,
    { command: 'research_request_more_info' },
  );
  return requestMoreInfoResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/**
 * The evidence-drawer query (T01). Disabled until a row is selected (rowId>0) so
 * opening the drawer is the trigger; `enabled` mirrors the open state.
 */
export function useEvidenceDrawer(
  requestId: number,
  rowId: number | null,
  enabled: boolean = true,
): UseQueryResult<EvidenceDrawerResponse, ApiResultError> {
  const id = rowId ?? 0;
  return useQuery<EvidenceDrawerResponse, ApiResultError>({
    queryKey: evidenceKeys.drawer(requestId, id),
    queryFn: () => fetchEvidenceDrawer(requestId, id),
    enabled: enabled && requestId > 0 && id > 0,
  });
}

/**
 * "Request more info" mutation (T03). On success writes the re-projected drawer
 * into the cached drawer query (so the open drawer re-renders the upgraded
 * criterion + the green trust banner) and invalidates the Review list (so the row
 * loses its amber needs-human tint).
 */
export function useRequestMoreInfo(
  requestId: number,
): UseMutationResult<RequestMoreInfoResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<RequestMoreInfoResponse, ApiResultError, number>({
    mutationFn: (rowId) => requestMoreInfo(requestId, rowId),
    onSuccess: (res, rowId) => {
      queryClient.setQueryData<EvidenceDrawerResponse>(evidenceKeys.drawer(requestId, rowId), {
        ok: true,
        drawer: res.drawer,
      });
      // The row's needs_human may have flipped → refresh the Review table tint/badges.
      void queryClient.invalidateQueries({ queryKey: reviewKeys.list(requestId) });
    },
  });
}
